import { useAgendaSettingsContext } from './Context';

export default function AgendaSettingsGeral() {
    const {
        daysOpen,
        setDaysOpen,
        submitGeral,
        loading,
        canEditAgendaSettings,
        canEditEstablishmentAgendaSettings,
        scheduleGap,
        setScheduleGap,
        minHour,
        setMinHour,
        maxHour,
        setMaxHour,
        gapBetweenSlots,
        setGapBetweenSlots,
        setMinAdvanceTime,
        minAdvanceTime,
        maxScheduleDate,
        setMaxScheduleDate,
        subscriptionOnlyScheduling,
        setSubscriptionOnlyScheduling,
        subscriptionServicesOnly,
        setSubscriptionServicesOnly,
    } = useAgendaSettingsContext();

    const hourOptions = Array.from({ length: 49 }, (_, index) => index * 30);
    const formatHour = (minutes: number) => {
        if (minutes === 24 * 60) return '24:00';
        const hours = Math.floor(minutes / 60).toString().padStart(2, '0');
        const rest = (minutes % 60).toString().padStart(2, '0');
        return `${hours}:${rest}`;
    };

    return (
        <div className="w-full space-y-4">
            <div className="rounded-xl border border-base-200 bg-base-100 p-4">
                <div className="form-control">
                    <div className="label items-start">
                        <span>
                            <span className="label-text block">Limites visíveis da agenda</span>
                            <span className="mt-1 block text-sm text-base-content/60">
                                Defina o primeiro e o último horário exibidos na régua da agenda do estabelecimento.
                            </span>
                        </span>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <label className="form-control">
                            <span className="label-text mb-1 text-sm">Horário mínimo</span>
                            <select
                                className="select select-bordered w-full"
                                value={minHour}
                                onChange={(e) => setMinHour(Number(e.target.value))}
                                disabled={loading || !canEditEstablishmentAgendaSettings}
                            >
                                {hourOptions.slice(0, -1).map((minutes) => (
                                    <option key={minutes} value={minutes}>{formatHour(minutes)}</option>
                                ))}
                            </select>
                        </label>
                        <label className="form-control">
                            <span className="label-text mb-1 text-sm">Horário máximo</span>
                            <select
                                className="select select-bordered w-full"
                                value={maxHour}
                                onChange={(e) => setMaxHour(Number(e.target.value))}
                                disabled={loading || !canEditEstablishmentAgendaSettings}
                            >
                                {hourOptions.slice(1).map((minutes) => (
                                    <option key={minutes} value={minutes}>{formatHour(minutes)}</option>
                                ))}
                            </select>
                        </label>
                    </div>
                    {!canEditEstablishmentAgendaSettings && (
                        <span className="mt-2 text-xs text-base-content/60">
                            Apenas administradores podem alterar os limites do estabelecimento.
                        </span>
                    )}
                </div>
            </div>

            <div className="rounded-xl border border-base-200 bg-base-100 p-4">
                <div className="form-control">
                    <label className="label" htmlFor="daysOpen">
                        <span className="label-text">Dias que a agenda ficará aberta</span>
                    </label>
                    <input
                        type="number"
                        value={daysOpen}
                        onChange={(e) => setDaysOpen(parseInt(e.target.value))}
                        id="daysOpen"
                        className="input input-bordered w-full"
                        min="1"
                        disabled={loading || !canEditAgendaSettings}
                    />
                    <span className="text-sm text-base-content/60">
                        Defina o número de dias que a agenda estará disponível para agendamento antecipado pelos
                        clientes.
                    </span>
                </div>
            </div>

            <div className="rounded-xl border border-base-200 bg-base-100 p-4">
                <div className="form-control">
                    <label className="label" htmlFor="maxScheduleDate">
                        <span className="label-text">Data limite para agendamentos</span>
                    </label>
                    <input
                        type="date"
                        id="maxScheduleDate"
                        className="input input-bordered w-full"
                        value={maxScheduleDate ?? ''}
                        onChange={(e) => setMaxScheduleDate(e.target.value || null)}
                        disabled={loading || !canEditAgendaSettings}
                    />
                    <span className="text-sm text-base-content/60">
                        Após essa data, novos agendamentos não poderão ser criados. Deixe em branco para não limitar
                        por data.
                    </span>
                </div>
            </div>

            <div className="rounded-xl border border-base-200 bg-base-100 p-4">
                <div className="form-control">
                    <label className="label" htmlFor="scheduleGap">
                        <span className="label-text">Passo dos horários disponíveis (em minutos)</span>
                    </label>
                    <input
                        type="number"
                        value={scheduleGap}
                        onChange={(e) => setScheduleGap(parseInt(e.target.value))}
                        id="scheduleGap"
                        className="input input-bordered w-full"
                        min="10"
                        disabled={loading || !canEditAgendaSettings}
                    />
                    <span className="text-sm text-base-content/60">
                        Define a grade de horários. Ex.: 10 → 08:00, 08:10, 08:20.
                    </span>
                </div>
            </div>

            <div className="rounded-xl border border-base-200 bg-base-100 p-4">
                <div className="form-control">
                    <label className="label" htmlFor="gapBetweenSlots">
                        <span className="label-text">Intervalo entre atendimentos (em minutos)</span>
                    </label>
                    <input
                        type="number"
                        value={gapBetweenSlots}
                        onChange={(e) => setGapBetweenSlots(parseInt(e.target.value) || 0)}
                        id="gapBetweenSlots"
                        className="input input-bordered w-full"
                        min="0"
                        disabled={loading || !canEditAgendaSettings}
                    />
                    <span className="text-sm text-base-content/60">
                        Tempo extra para organização/limpeza entre atendimentos.
                    </span>
                </div>
            </div>

            <div className="rounded-xl border border-base-200 bg-base-100 p-4">
                <div className="form-control">
                    <label className="label" htmlFor="minAdvanceTime">
                        <span className="label-text">Tempo minimo de antecedencia (em minutos)</span>
                    </label>
                    <input
                        type="number"
                        value={minAdvanceTime}
                        onChange={(e) => setMinAdvanceTime(parseInt(e.target.value))}
                        id="minAdvanceTime"
                        className="input input-bordered w-full"
                        min="0"
                        disabled={loading || !canEditAgendaSettings}
                    />
                    <span className="text-sm text-base-content/60">
                        Defina quantos minutos antes um cliente pode agendar um horário.
                    </span>
                </div>
            </div>

            <div className="rounded-xl border border-base-200 bg-base-100 p-4">
                <div className="form-control">
                    <label className="cursor-pointer flex items-start gap-4">
                        <input
                            type="checkbox"
                            className="toggle toggle-primary mt-1"
                            checked={subscriptionOnlyScheduling}
                            onChange={() => setSubscriptionOnlyScheduling((value) => !value)}
                            disabled={loading || !canEditAgendaSettings}
                        />
                        <span>
                            <span className="block text-sm font-medium text-base-content">
                                Liberar agenda apenas para clientes com assinatura
                            </span>
                            <span className="mt-1 block text-sm text-base-content/60">
                                Clientes sem assinatura ativa não verão horários disponíveis desta agenda.
                            </span>
                        </span>
                    </label>
                </div>
            </div>

            <div className="rounded-xl border border-base-200 bg-base-100 p-4">
                <div className="form-control">
                    <label className="cursor-pointer flex items-start gap-4">
                        <input
                            type="checkbox"
                            className="toggle toggle-primary mt-1"
                            checked={subscriptionServicesOnly}
                            onChange={() => setSubscriptionServicesOnly((value) => !value)}
                            disabled={loading || !canEditAgendaSettings}
                        />
                        <span>
                            <span className="block text-sm font-medium text-base-content">
                                Assinantes só podem agendar serviços do plano
                            </span>
                            <span className="mt-1 block text-sm text-base-content/60">
                                Quando o cliente tiver assinatura ativa, a seleção fica restrita aos serviços
                                contemplados no plano.
                            </span>
                        </span>
                    </label>
                </div>
            </div>

            <div className="mt-6 flex justify-end">
                <button
                    onClick={submitGeral}
                    disabled={loading || !canEditAgendaSettings}
                    className="btn btn-primary shadow-md w-full sm:w-auto min-w-[160px]"
                    type="submit"
                >
                    Salvar alterações
                </button>
            </div>
        </div>
    );
}
